import { defineCopy } from '../pageCopy';

/**
 * Copy for the /services index (en, ar, ckb).
 *
 * The English page keeps reading service titles and descriptions from
 * `src/lib/data.ts` (and the hand-wired social-ai entry) so its output stays
 * byte-identical. The `en` tree below mirrors that text with the source's
 * em dashes turned into commas or colons, because the copy guard test bans
 * dash punctuation; it is only used by tests and as the key-shape reference.
 */
const en = {
  meta: {
    title: 'Services',
    description:
      'Datacode services: mobile app development, web design, AI integration, UI/UX, e-commerce, digital strategy, and more, delivered from Erbil for clients across the Middle East and beyond.',
    ogDescription:
      'Mobile, web, AI, and digital strategy services. Each engagement is paired with the case studies that prove it.',
    twitterDescription: 'Mobile, web, AI, and digital strategy services from Datacode.',
  },
  crumbHome: 'Home',
  hero: {
    eyebrow: 'Services',
    title: 'What we build',
    intro:
      'Eleven ways we take a problem and ship the thing that solves it, designed, built, and supported from our studio in Erbil. Every service links to the case studies that prove it.',
  },
  groups: {
    build: 'Build',
    intelligence: 'Intelligence',
    design: 'Design & Strategy',
    growth: 'Growth',
  },
  items: {
    'mobile-app-development': {
      title: 'Mobile App Development',
      description:
        'You get a real app on the App Store and Google Play, one codebase, both platforms. We build it, ship it, and stay on for the updates after launch.',
    },
    'web-design-development': {
      title: 'Web Design & Development',
      description:
        'A website or web app that loads fast, works on every screen, and reads like your brand wrote it. We design and build it, then hand you the keys.',
    },
    'e-commerce-solutions': {
      title: 'E-Commerce Solutions',
      description:
        'An online store that takes payment, tracks stock, and shows you what is selling. Built to handle local payment methods, not just the ones that work in Silicon Valley.',
    },
    'humanitarian-charity-app-development': {
      title: 'Humanitarian and Charity App Development',
      description:
        'Apps for NGOs and charities that work on a tight budget and in places with bad connectivity. We have built for this region, so we know what breaks in the field.',
    },
    'ai-integration': {
      title: 'AI Integration',
      description:
        'AI that does a specific job inside your product, answering customers, sorting documents, pulling answers out of your own data. We scope it to one real task and wire it in.',
    },
    'data-analytics': {
      title: 'Data Analytics',
      description:
        'A dashboard that answers the questions you actually ask, what sold, what stalled, what to do next, pulled live from your own systems.',
    },
    'ui-ux-design': {
      title: 'UI/UX Design',
      description:
        'Screens people understand without a manual. We map the flows, design the interface, and hand off files your developers can build from, or we build them ourselves.',
    },
    'digital-strategy-consultation': {
      title: 'Digital Strategy & Consultation',
      description:
        'You sit down with the engineers who would actually build it. We tell you what to build first, what to skip, and which tech choices you will regret in a year.',
    },
    'social-ai': {
      title: 'AI Social Media Management',
      description:
        'AI-native social media management proven with real client numbers: verification, one CRM for every lead, targeted posts, and a monthly report.',
    },
    'social-media-management': {
      title: 'Social Media Management',
      description:
        'We run your social accounts: plan the posts, write them, publish them, so the feed stays alive without you watching it every day.',
    },
    'content-creation': {
      title: 'Content Creation',
      description:
        'Words, images, and video that sound like you and say something. We make the content; you stop staring at a blank page.',
    },
  },
  footer: {
    eyebrow: 'Start a project',
    line1: 'Not sure which one you need?',
    line2: 'Describe the problem, we’ll scope it.',
    cta: 'Tell us about your project',
  },
};

const ar: typeof en = {
  meta: {
    title: 'الخدمات',
    description:
      'خدمات Datacode: تطوير تطبيقات الموبايل، تصميم المواقع، دمج الذكاء الاصطناعي، تصميم UI/UX، التجارة الإلكترونية، والاستراتيجية الرقمية وغيرها، نقدمها من أربيل لعملاء في الشرق الأوسط وخارجه.',
    ogDescription:
      'خدمات الموبايل والويب والذكاء الاصطناعي والاستراتيجية الرقمية. كل خدمة مرتبطة بدراسات الحالة التي تثبتها.',
    twitterDescription: 'خدمات الموبايل والويب والذكاء الاصطناعي والاستراتيجية الرقمية من Datacode.',
  },
  crumbHome: 'الرئيسية',
  hero: {
    eyebrow: 'الخدمات',
    title: 'ما نبنيه',
    intro:
      'إحدى عشرة طريقة نأخذ بها مشكلتك ونسلّمك ما يحلّها، نصمّمه ونبنيه وندعمه من استوديو أربيل. وكل خدمة مرتبطة بدراسات الحالة التي تثبتها.',
  },
  groups: {
    build: 'البناء',
    intelligence: 'الذكاء',
    design: 'التصميم والاستراتيجية',
    growth: 'النمو',
  },
  items: {
    'mobile-app-development': {
      title: 'تطوير تطبيقات الموبايل',
      description:
        'تحصل على تطبيق حقيقي على App Store وGoogle Play، بقاعدة كود واحدة لكلا المنصتين. نبنيه ونطلقه ونبقى معك لتحديثات ما بعد الإطلاق.',
    },
    'web-design-development': {
      title: 'تصميم وتطوير مواقع الويب',
      description:
        'موقع أو تطبيق ويب يفتح بسرعة، ويشتغل على كل الشاشات، وتحسّه مكتوباً بلسان علامتك. نصمّمه ونبنيه ثم نسلّمك المفاتيح.',
    },
    'e-commerce-solutions': {
      title: 'حلول التجارة الإلكترونية',
      description:
        'متجر إلكتروني يستلم الدفع، ويتتبع المخزون، ويوضح لك ما الذي يُباع. مبني للتعامل مع وسائل الدفع المحلية، وليس فقط تلك التي تنجح في وادي السيليكون.',
    },
    'humanitarian-charity-app-development': {
      title: 'تطوير التطبيقات الإنسانية والخيرية',
      description:
        'تطبيقات للمنظمات غير الحكومية والجمعيات الخيرية، تعمل بميزانية محدودة وفي أماكن ضعيفة الاتصال. بنينا لهذه المنطقة، فنعرف ما الذي يتعطل في الميدان.',
    },
    'ai-integration': {
      title: 'دمج الذكاء الاصطناعي',
      description:
        'ذكاء اصطناعي يؤدي مهمة محددة داخل منتجك: يجاوب الزبائن، يرتّب المستندات، ويستخرج الإجابات من بياناتك أنت. نحدّد له مهمة حقيقية واحدة ونربطه بمنتجك.',
    },
    'data-analytics': {
      title: 'تحليل البيانات',
      description:
        'لوحة معلومات تجاوب على الأسئلة التي تطرحها فعلاً: ما الذي بِيع، وما الذي تعثّر، وماذا تفعل بعدها، تُسحب مباشرة من أنظمتك أنت.',
    },
    'ui-ux-design': {
      title: 'تصميم UI/UX',
      description:
        'شاشات يفهمها الناس بدون دليل استخدام. نرسم مسارات الاستخدام، نصمّم الواجهة، ونسلّم ملفات يبني عليها مطوّروك، أو نبنيها بأنفسنا.',
    },
    'digital-strategy-consultation': {
      title: 'الاستراتيجية الرقمية والاستشارات',
      description:
        'تجلس مع المهندسين الذين سيبنون مشروعك فعلاً. نقول لك ماذا تبني أولاً، وماذا تتخطى، وأي خيارات تقنية ستندم عليها بعد سنة.',
    },
    'social-ai': {
      title: 'إدارة السوشيال ميديا بالذكاء الاصطناعي',
      description:
        'إدارة سوشيال ميديا مبنية على الذكاء الاصطناعي ومثبتة بأرقام عملاء حقيقية: توثيق الحسابات، وCRM واحد لكل عميل محتمل، ومنشورات موجّهة، وتقرير شهري.',
    },
    'social-media-management': {
      title: 'إدارة السوشيال ميديا',
      description:
        'ندير حساباتك على السوشيال ميديا: نخطط للمنشورات ونكتبها وننشرها، حتى يبقى حسابك حيّاً بدون ما تتابعه كل يوم.',
    },
    'content-creation': {
      title: 'صناعة المحتوى',
      description:
        'كلمات وصور وفيديو تشبهك وتقول شيئاً. نحن نصنع المحتوى، وأنت تتوقف عن التحديق في صفحة بيضاء.',
    },
  },
  footer: {
    eyebrow: 'ابدأ مشروعك',
    line1: 'لست متأكداً أي خدمة تحتاج؟',
    line2: 'صِف لنا المشكلة، ونحن نحدد نطاق العمل.',
    cta: 'أخبرنا عن مشروعك',
  },
};

const ckb: typeof en = {
  meta: {
    title: 'خزمەتگوزارییەکان',
    description:
      'خزمەتگوزارییەکانی Datacode: گەشەپێدانی ئەپی مۆبایل، دیزاینی وێبسایت، تێکەڵکردنی زیرەکی دەستکرد، دیزاینی UI/UX، بازرگانی ئەلیکترۆنی، ستراتیژی دیجیتاڵ و زیاتر، لە هەولێرەوە پێشکەش دەکرێن بۆ کڕیاران لە ناوچەکە و دەرەوەی.',
    ogDescription:
      'خزمەتگوزاری مۆبایل و وێب و زیرەکی دەستکرد و ستراتیژی دیجیتاڵ. هەر خزمەتگوزارییەک بە ئەو کەیسانەوە بەستراوەتەوە کە دەیسەلمێنن.',
    twitterDescription: 'خزمەتگوزاری مۆبایل و وێب و زیرەکی دەستکرد و ستراتیژی دیجیتاڵ لە Datacode.',
  },
  crumbHome: 'سەرەکی',
  hero: {
    eyebrow: 'خزمەتگوزارییەکان',
    title: 'چی دروست دەکەین',
    intro:
      'یازدە شێواز هەیە کە کێشەکەت وەردەگرین و ئەو شتەی چارەسەری دەکات دروستی دەکەین و دەیگەیەنین، دیزاین و دروستکردن و پشتگیری لە ستۆدیۆی هەولێرەوە. هەر خزمەتگوزارییەک بە ئەو کەیسانەوە بەستراوەتەوە کە دەیسەلمێنن.',
  },
  groups: {
    build: 'دروستکردن',
    intelligence: 'زیرەکی',
    design: 'دیزاین و ستراتیژی',
    growth: 'گەشەکردن',
  },
  items: {
    'mobile-app-development': {
      title: 'گەشەپێدانی ئەپی مۆبایل',
      description:
        'ئەپێکی ڕاستەقینەت دەستدەکەوێت لەسەر App Store و Google Play، بە یەک کۆد بۆ هەردوو پلاتفۆرم. ئێمە دروستی دەکەین، بڵاوی دەکەینەوە و دوای بڵاوکردنەوەش بۆ نوێکردنەوەکان لەگەڵتدا دەمێنینەوە.',
    },
    'web-design-development': {
      title: 'دیزاین و گەشەپێدانی وێبسایت',
      description:
        'ماڵپەڕ یان ئەپی وێبێک کە خێرا دەکرێتەوە، لەسەر هەموو شاشەیەک کار دەکات و وا دەخوێنرێتەوە وەک ئەوەی براندەکەت خۆی نووسیبێتی. دیزاین و دروستی دەکەین، پاشان کلیلەکانت دەدەینێ.',
    },
    'e-commerce-solutions': {
      title: 'چارەسەری بازرگانی ئەلیکترۆنی',
      description:
        'دوکانێکی ئۆنلاین کە پارە وەردەگرێت، کۆگا بەدواداچوون دەکات و پیشانت دەدات چی دەفرۆشرێت. بۆ شێوازە خۆجێییەکانی پارەدان دروستکراوە، نەک تەنها ئەوانەی لە سیلیکۆن ڤالی کار دەکەن.',
    },
    'humanitarian-charity-app-development': {
      title: 'گەشەپێدانی ئەپی مرۆیی و خێرخوازی',
      description:
        'ئەپ بۆ ڕێکخراوە ناحکومییەکان و دامەزراوە خێرخوازییەکان کە بە بودجەی سنووردار و لە شوێنی ئینتەرنێت لاواز کار دەکەن. بۆ ئەم ناوچەیە دروستمان کردووە، بۆیە دەزانین لە مەیدان چی تێک دەچێت.',
    },
    'ai-integration': {
      title: 'تێکەڵکردنی زیرەکی دەستکرد',
      description:
        'زیرەکی دەستکردێک کە کارێکی دیاریکراو لەناو بەرهەمەکەتدا دەکات: وەڵامی کڕیاران دەداتەوە، بەڵگەنامەکان ڕیز دەکات و وەڵام لە داتای خۆتەوە دەردەهێنێت. یەک کاری ڕاستەقینە دیاری دەکەین و دەیبەستینەوە بە بەرهەمەکەتەوە.',
    },
    'data-analytics': {
      title: 'شیکاری داتا',
      description:
        'داشبۆردێک کە وەڵامی ئەو پرسیارانە دەداتەوە کە بەڕاستی دەیانکەیت: چی فرۆشرا، چی وەستا، دواتر چی بکەیت، ڕاستەوخۆ لە سیستەمەکانی خۆتەوە دەهێنرێت.',
    },
    'ui-ux-design': {
      title: 'دیزاینی UI/UX',
      description:
        'شاشەگەلێک کە خەڵک بێ ڕێنمایی تێیان دەگات. ڕێڕەوی بەکارهێنان دەکێشین، ڕووکار دیزاین دەکەین و فایلی ئامادە دەدەین کە گەشەپێدەرەکانت لەسەری دروست بکەن، یان خۆمان دروستی دەکەین.',
    },
    'digital-strategy-consultation': {
      title: 'ستراتیژی دیجیتاڵ و ڕاوێژکاری',
      description:
        'لەگەڵ ئەو ئەندازیارانە دادەنیشیت کە بەڕاستی پرۆژەکەت دروست دەکەن. پێت دەڵێین چی یەکەم دروست بکەیت، چی جێبهێڵیت و کام هەڵبژاردنی تەکنەلۆجی دوای ساڵێک پەشیمانت دەکاتەوە.',
    },
    'social-ai': {
      title: 'بەڕێوەبردنی سۆشیاڵ میدیا بە زیرەکی دەستکرد',
      description:
        'بەڕێوەبردنی سۆشیاڵ میدیای بنیاتنراو لەسەر زیرەکی دەستکرد، بە ژمارەی کڕیارانی ڕاستەقینە سەلمێنراوە: پشتڕاستکردنەوەی ئەکاونت، یەک CRM بۆ هەموو لیدێک، پۆستی ئامانجدار و ڕاپۆرتی مانگانە.',
    },
    'social-media-management': {
      title: 'بەڕێوەبردنی سۆشیاڵ میدیا',
      description:
        'ئێمە ئەکاونتەکانی سۆشیاڵ میدیات بەڕێوە دەبەین: پلانی پۆستەکان دادەنێین، دەیاننووسین و بڵاویان دەکەینەوە، بۆ ئەوەی ئەکاونتەکەت زیندوو بمێنێتەوە بێ ئەوەی ڕۆژانە چاودێری بکەیت.',
    },
    'content-creation': {
      title: 'دروستکردنی ناوەڕۆک',
      description:
        'وشە و وێنە و ڤیدیۆ کە لە خۆت دەچن و شتێک دەڵێن. ئێمە ناوەڕۆک دروست دەکەین، تۆش وازدەهێنیت لە تەماشاکردنی لاپەڕەیەکی سپی.',
    },
  },
  footer: {
    eyebrow: 'پرۆژەیەک دەست پێ بکە',
    line1: 'دڵنیا نیت کامیان پێویستە؟',
    line2: 'کێشەکە باس بکە، ئێمە قەبارەی کارەکە دیاری دەکەین.',
    cta: 'دەربارەی پرۆژەکەت پێمان بڵێ',
  },
};

export const servicesCopy = defineCopy(en, ar, ckb);
